"use client";

/**
 * The builder's projects as site boards: picture, status, where, how far
 * along (measured from the daily reports) and what is for sale. A board
 * opens the project: its pictures and latest site photos, the stages, and
 * the homes still for sale on it.
 */

import { useEffect, useRef, useState } from "react";
import { ArrowRight, Building2, HardHat, X } from "lucide-react";
import { PROJECT_STATUS_LABEL, type BuilderProject, type BuilderUnit } from "@/lib/config/builderSite";
import { askAbout, floorLabel, foreignShort, ugxShort } from "../theme";
import HomePrice from "./HomePrice";

const STATUS_STYLE: Record<BuilderProject["status"], string> = {
  building: "bg-[var(--b-hiviz)] text-[var(--b-ink)]",
  coming: "bg-white text-[var(--b-ink)]",
  onhold: "bg-[#e5e7eb] text-[var(--b-ink)]",
  finished: "bg-[var(--b-ink)] text-white",
};

function Progress({ pct, className = "" }: { pct: number; className?: string }) {
  return (
    <div className={`h-2 overflow-hidden rounded-full bg-[var(--b-line)] ${className}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="h-full rounded-full bg-[var(--b-accent)]" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function ProjectsSection({ projects, units }: { projects: BuilderProject[]; units: BuilderUnit[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const project = projects.find((p) => p.code === open) ?? null;
  return (
    <>
      <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <li key={p.code} className="min-w-0">
            <button
              type="button"
              onClick={() => setOpen(p.code)}
              className="group grid h-full w-full content-start overflow-hidden rounded-xl border-2 border-[var(--b-ink)] bg-white text-left transition-shadow hover:shadow-[0_16px_32px_-22px_rgba(0,0,0,.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--b-accent-ink)]"
            >
              <div className="relative">
                {p.cover ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={p.cover} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                ) : (
                  <div className="grid aspect-[4/3] place-items-center bg-[var(--b-ground)] text-[var(--b-muted)]">
                    <Building2 className="h-12 w-12" />
                  </div>
                )}
                <span className={`absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-extrabold uppercase tracking-wide ${STATUS_STYLE[p.status]}`}>
                  {PROJECT_STATUS_LABEL[p.status]}
                </span>
              </div>
              <div aria-hidden className="h-2 bg-[repeating-linear-gradient(-45deg,var(--b-hiviz)_0_10px,var(--b-ink)_10px_20px)]" />
              <div className="grid gap-3 p-4">
                <div className="grid gap-0.5">
                  <h3 className="text-lg font-extrabold leading-tight">{p.name}</h3>
                  <p className="text-sm text-[var(--b-muted)]">{[p.area, p.typeLabel].filter(Boolean).join(" · ")}</p>
                </div>
                {p.pct !== undefined && (
                  <div className="grid gap-1.5">
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-bold tabular-nums">{p.pct}% built</span>
                      <span className="text-[var(--b-muted)]">{p.finishedMonth ? `Finished ${p.finishedMonth}` : p.readyMonth ? `Ready ${p.readyMonth}` : ""}</span>
                    </div>
                    <Progress pct={p.pct} />
                  </div>
                )}
                {p.pct === undefined && (p.readyMonth || p.startMonth) && (
                  <p className="text-sm text-[var(--b-muted)]">{p.readyMonth ? `Ready ${p.readyMonth}` : `Starts ${p.startMonth}`}</p>
                )}
                {p.units && p.units.available > 0 && (
                  <p className="text-sm font-semibold">
                    {p.units.available} {p.units.available === 1 ? "home" : "homes"} for sale
                    {p.units.fromPrice ? (
                      <span className="font-normal text-[var(--b-muted)]">
                        {" "}· from {p.units.fromForeign?.first ? foreignShort(p.units.fromForeign) : ugxShort(p.units.fromPrice)}
                        {p.units.fromForeign ? ` (${p.units.fromForeign.first ? ugxShort(p.units.fromPrice) : foreignShort(p.units.fromForeign)})` : ""}
                      </span>
                    ) : null}
                  </p>
                )}
                <span className="inline-flex items-center gap-1 text-sm font-bold text-[var(--b-accent-ink)]">
                  View the project <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>
      {project && <ProjectDialog project={project} units={units.filter((u) => u.project === project.code)} onClose={() => setOpen(null)} />}
    </>
  );
}

function ProjectDialog({ project: p, units, onClose }: { project: BuilderProject; units: BuilderUnit[]; onClose: () => void }) {
  const gallery = [...p.pictures.map((url) => ({ url, caption: "" })), ...p.photos.map((ph) => ({ url: ph.url, caption: `On site, ${ph.day}` }))];
  const [shown, setShown] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closeFn = useRef(onClose);
  closeFn.current = onClose;

  // While open: the page behind stays still, Escape closes, focus starts on Close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeFn.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const facts = [
    ["Type", p.typeLabel],
    ["Where", p.area],
    ["Floors", p.floors ? String(p.floors) : undefined],
    ["Homes", p.unitsTotal ? String(p.unitsTotal) : undefined],
    ["Started", p.startMonth],
    [p.finishedMonth ? "Finished" : "Ready", p.finishedMonth ?? p.readyMonth],
  ].filter((f): f is [string, string] => !!f[1]);
  const current = gallery[Math.min(shown, Math.max(0, gallery.length - 1))];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={p.name}
        onClick={(e) => e.stopPropagation()}
        className="grid max-h-[92vh] w-full max-w-4xl grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-t-2xl bg-white text-[var(--b-ink)] sm:rounded-2xl"
      >
        <div className="flex items-center gap-3 border-b border-[var(--b-line)] px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-extrabold">{p.name}</h3>
            <p className="truncate text-sm text-[var(--b-muted)]">{[PROJECT_STATUS_LABEL[p.status], p.area].filter(Boolean).join(" · ")}</p>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[var(--b-line)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid content-start gap-6 overflow-y-auto px-4 py-5 sm:px-6">
          {current ? (
            <figure className="grid gap-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={current.url} alt={current.caption || p.name} className="aspect-[16/10] w-full rounded-lg bg-[var(--b-ground)] object-cover" />
              {current.caption && <figcaption className="text-sm text-[var(--b-muted)]">{current.caption}</figcaption>}
              {gallery.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {gallery.map((g, i) => (
                    <button
                      key={g.url}
                      type="button"
                      onClick={() => setShown(i)}
                      aria-label={g.caption || `Picture ${i + 1}`}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 ${i === shown ? "border-[var(--b-accent)]" : "border-transparent"}`}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={g.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </figure>
          ) : (
            <div className="grid aspect-[16/10] place-items-center rounded-lg bg-[var(--b-ground)] text-[var(--b-muted)]">
              <HardHat className="h-14 w-14" />
            </div>
          )}

          {p.blurb && <p className="max-w-3xl whitespace-pre-line leading-relaxed">{p.blurb}</p>}

          {facts.length > 0 && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-[var(--b-line)] p-4 sm:grid-cols-3">
              {facts.map(([k, v]) => (
                <div key={k} className="grid gap-0.5">
                  <dt className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--b-muted)]">{k}</dt>
                  <dd className="font-semibold">{v}</dd>
                </div>
              ))}
            </dl>
          )}

          {p.stages && p.stages.length > 0 && (
            <div className="grid gap-3">
              <h4 className="flex items-baseline justify-between gap-3 text-sm font-extrabold uppercase tracking-[0.12em]">
                Stage by stage
                {p.pct !== undefined && <span className="tabular-nums normal-case tracking-normal">{p.pct}% overall</span>}
              </h4>
              <ul className="grid gap-2.5">
                {p.stages.map((s) => (
                  <li key={s.name} className="grid grid-cols-[minmax(0,1fr)_3.5rem] items-center gap-x-3 gap-y-1 text-sm">
                    <span className="truncate">{s.name}</span>
                    <span className="text-right font-semibold tabular-nums">{s.pct === 100 ? "Done" : `${s.pct}%`}</span>
                    <Progress pct={s.pct} className="col-span-2" />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {units.length > 0 && (
            <div className="grid gap-3">
              <h4 className="text-sm font-extrabold uppercase tracking-[0.12em]">For sale on {p.name}</h4>
              <div className="overflow-x-auto rounded-lg border border-[var(--b-line)]">
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="bg-[var(--b-ground)] text-left text-xs uppercase tracking-wide text-[var(--b-muted)]">
                    <tr>
                      <th className="px-3 py-2 font-bold">Home</th>
                      <th className="px-3 py-2 font-bold">Bedrooms</th>
                      <th className="px-3 py-2 text-right font-bold">Floor area</th>
                      <th className="px-3 py-2 text-right font-bold">Price</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {units.map((u) => (
                      <tr key={u.label} className="border-t border-[var(--b-line)]">
                        <td className="px-3 py-2.5 font-semibold">
                          {u.label}
                          <span className="block text-xs font-normal text-[var(--b-muted)]">{[u.block, floorLabel(u.floor)].filter((x) => x && x !== "—").join(" · ")}</span>
                        </td>
                        <td className="px-3 py-2.5">{u.bedrooms ?? "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {u.areaM2} m²
                          {u.plot && <span className="block text-xs text-[var(--b-muted)]">Plot {u.plot}</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                          <HomePrice price={u.price} foreign={u.foreign} reserved={u.status === "reserved"} />
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {u.status === "available" && (
                            <button
                              type="button"
                              onClick={() => {
                                onClose();
                                askAbout(p.code, u.label);
                              }}
                              className="font-bold text-[var(--b-accent-ink)] hover:underline"
                            >
                              Ask
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 border-t border-[var(--b-line)] pt-5">
            <button
              type="button"
              onClick={() => {
                onClose();
                askAbout(p.code);
              }}
              className="rounded-lg bg-[var(--b-accent)] px-5 py-2.5 text-sm font-bold text-[var(--b-on-accent)]"
            >
              Ask about {p.name}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
